import { IImageBackgroundData, IPageBackgroundData, IVideoBackgroundData } from "@pageBuilder/typings";
import { RendererBase } from "src/shared/renderer-base";
import { CssProperties } from "src/models/css-property";

export class PageBackgroundRenderer extends RendererBase<IPageBackgroundData> {

    private static PAGE_BACKGROUND_ID = "page-background";

    protected override createNewInternal(): HTMLElement {
        const element = document.createElement("div");
        element.classList.add("background");
        element.setAttribute("id", PageBackgroundRenderer.PAGE_BACKGROUND_ID);
        element.setAttribute("data-page-background", "");

        return element;
    }

    protected override renderInternal(element: HTMLElement, data: IPageBackgroundData): HTMLElement {
        this.renderMediaBackground(element, data);
        this.setBackgroundStyle(element, data);
        return element;
    }

    private renderMediaBackground(element: HTMLElement, data: IPageBackgroundData): void {
        const existedImage = element.querySelector("img");
        const existedVideo = element.querySelector("video");
        const existedMediaBackground = existedImage ? "image" : (existedVideo ? "video" : null);

        if (existedMediaBackground !== data?.mediaBackground?.type) {
            this.cleanupMediaBackground(element);
        }

        if (data?.mediaBackground?.type === "image") {
            this.renderImageBackground(element, data.mediaBackground);
        } else if (data?.mediaBackground?.type === "video") {
            this.renderVideoBackground(element, data.mediaBackground);
        }
    }

    private renderImageBackground(element: HTMLElement, data: IImageBackgroundData): void {
        let image = element?.querySelector("img");
        if (!image) {
            image = document.createElement("img");
            image.classList.add("background-image");
            element.appendChild(image);
        }

        if (image.src !== data.src) {
            image.src = data.src ?? "";
            image.alt = "page background";
        }
        this.setImageStyle(element, data);
    }

    private renderVideoBackground(element: HTMLElement, data: IVideoBackgroundData): void {
        const existedVideo = element?.querySelector("video");
        if (!existedVideo) {
            const video = document.createElement("video");
            video.src = data.src ?? "";
            video.muted = true;
            video.controls = false;
            video.playsInline = true;
            video.setAttribute("autoplay", "");
            video.setAttribute("loop", "true");
            video.setAttribute("muted", "true");
            if (data.type === "video") {
                video.setAttribute("poster", data.thumbnailUrl ?? "");
            }
            element.appendChild(video);
        } else if (existedVideo?.src != data.src) {
            existedVideo.src = data.src ?? "";
        }

        this.setVideoStyle(element, data);
    }

    private setBackgroundStyle(element: HTMLElement, data: IPageBackgroundData): void {
        this.cleanupStyle(element);

        if (!data?.style?.color) {
            return;
        }

        const props = new CssProperties();
        props.tryAdd("background-color", `${data.style.color}`);

        this.styleService.setStyle(element, `#${element.id}`, props.data);
    }

    private setImageStyle(element: HTMLElement, data: IImageBackgroundData): void {
        if (data?.type !== "image") {
            return;
        }

        const imageProps = new CssProperties();

        if (data.style?.position) {
            imageProps.tryAdd("object-position", `${data.style.position.offsetX}% ${data.style.position.offsetY}%`);
        }

        imageProps.tryAdd("opacity", `${data.style?.opacity ?? 1}`, null);
        this.styleService.setStyle(element, `#${element.id} img`, imageProps.data);
    }

    private setVideoStyle(element: HTMLElement, data: IVideoBackgroundData): void {
        if (data?.type !== "video") {
            return;
        }

        const videoProps = new CssProperties();
        videoProps.tryAdd("opacity", `${data.style?.opacity ?? 1}`, null);

        this.styleService.setStyle(element, `#${element.id} video`, videoProps.data);
    }

    private cleanupStyle(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id}`);
    }

    private cleanupMediaBackground(element: HTMLElement): void {
        Array.from(element.children).forEach(child => {
            child.remove();
        });

        this.cleanupImageStyle(element);
        this.cleanupVideoStyle(element);
    }

    private cleanupImageStyle(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} img`);
    }

    private cleanupVideoStyle(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} video`);
    }

    protected override cleanupInternal(element: HTMLElement): void {
        this.cleanupStyle(element);
        this.cleanupMediaBackground(element);
    }

    protected override getContentSelectorsInternal(element: HTMLElement): string[] {
        return [`#${element.id}`, `#${element.id} img`, `#${element.id} video`];
    }

}
